// 상세 Drawer + 약물반응(IC50) 탭
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "../IntegratedSearchPage.module.css";
import { labelFor, MUT_REP_GENES } from "../data/searchData";
import { Icon } from "./Icons";

const MUT_REP_SET = new Set(MUT_REP_GENES);

export function DetailDrawer({ group, selectedDrug, setSelectedDrug, fetchDetail, onClose }) {
    const { t } = useTranslation();
    const ngsList = group.ngsList || [];
    const [sampleName, setSampleName] = useState(
        (ngsList.find((n) => n.dataType === "WES") || ngsList[0])?.sampleName || null
    );
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);

    // 선택 샘플의 데이터 타입(WES/RNA)에 맞는 탭만 노출 (+ Drug 항상)
    const curNgs = ngsList.find((n) => n.sampleName === sampleName);
    const curType = curNgs?.dataType;            // 'WES' | 'RNA'
    const tabs = ["Info"];
    if (curType === "WES") tabs.push("WES");
    else if (curType === "RNA") tabs.push("RNA");
    tabs.push("Drug");
    const [tab, setTab] = useState(tabs[0]);

    // 샘플이 바뀌어 현재 탭이 사라지면 그 샘플의 데이터 탭으로 자동 전환
    useEffect(() => {
        if (tabs.indexOf(tab) === -1) setTab(tabs[0]);
    }, [sampleName]); // eslint-disable-line

    useEffect(() => {
        if (!sampleName) return;
        let alive = true;
        setLoading(true);
        fetchDetail(group.groupKey, sampleName)
            .then((d) => { if (alive) setDetail(d); })
            .catch(() => { if (alive) setDetail(null); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [group.groupKey, sampleName, fetchDetail]);

    const common = detail?.common || group;
    const wes = detail?.wes;
    const rna = detail?.rna;
    const drug = detail?.drug || group.drugIC50 || {};

    return (
        <>
            <div className={styles.drawerBackdrop} onClick={onClose} />
            <aside className={styles.drawer}>
                <div className={styles.drawerHeader}>
                    <div>
                        <div className={styles.drawerEyebrow}>{t('오가노이드')}</div>
                        <div className={styles.drawerTitleRow}>
                            <span className={styles.drawerTitle}>{group.id}</span>
                            <span className={styles.drawerSub}>{group.organ}</span>
                        </div>
                        <div className={styles.drawerBadgeRow}>
                            <span className={group.hasWES ? `${styles.ngsBadge} ${styles.ngsWes}` : `${styles.ngsBadge} ${styles.ngsOff}`}>WES</span>
                            <span className={group.hasRNA ? `${styles.ngsBadge} ${styles.ngsRna}` : `${styles.ngsBadge} ${styles.ngsOff}`}>RNA</span>
                        </div>
                    </div>
                    <button className={styles.iconBtn} onClick={onClose}><Icon.X /></button>
                </div>

                {/* 세부 NGS 코드 선택 */}
                <div className={styles.ngsPickRow}>
                    <span className={styles.fieldLabel} style={{ margin: 0 }}>{t('NGS 코드')}</span>
                    <select className={styles.drugSelectInline} value={sampleName || ""}
                        onChange={(e) => setSampleName(e.target.value || null)}>
                        {ngsList.map((n) => (
                            <option key={n.sampleName} value={n.sampleName}>
                                {n.sampleName} · {n.dataType}{n.passage ? ` · P${n.passage}` : ""}
                            </option>
                        ))}
                    </select>
                </div>

                <div className={styles.drawerTabs}>
                    {tabs.map((tb) => (
                        <button key={tb} className={tab === tb ? `${styles.drawerTab} ${styles.drawerTabActive}` : styles.drawerTab} onClick={() => setTab(tb)}>
                            {tb === "Drug" ? "Drug response (IC50)" : tb}
                        </button>
                    ))}
                </div>

                <div className={styles.drawerBody}>
                    {loading && <div className={styles.fieldHint}>{t('불러오는 중…')}</div>}

                    {tab === "Info" && (
                        <DrawerSection title={t('임상 정보')}>
                            <DGrid>
                                <DItem label={t('나이')}>{group.age != null ? `${group.age}y` : "—"}</DItem>
                                <DItem label={t('성별')}>{t(labelFor(group.gender))}</DItem>
                                <DItem label={t('장기')}>{group.organ}</DItem>
                                <DItem label={t('Sample type')}><span className={common.dx === "Primary" ? styles.dxPrimary : styles.dxMetastatic}><span className={styles.dot} /> {t(labelFor(common.dx))}</span></DItem>
                                <DItem label={t('Subtype')}>{common.subtype}</DItem>
                                <DItem label={t('Paired Normal (NT)')}>{t(labelFor(common.paired))}</DItem>
                                <DItem label="CRO">{common.croAvailable ? `${t('가능')}${common.croReason === "forced" ? ` (${t('지정')})` : ""}` : "—"}</DItem>
                            </DGrid>
                        </DrawerSection>
                    )}

                    {tab === "WES" && (
                        <>
                            <DrawerSection title="Mutation (WES)">
                                <div className={styles.mutationRow} style={{ marginBottom: 10 }}>
                                    {(() => {
                                        // 목록 화면과 동일 규칙: 대표 유전자 + 필터로 선택한 유전자만 노출,
                                        // 나머지는 "+N" 으로 접고 전체 목록은 title 로 확인.
                                        const all = group.mutations || [];
                                        if (all.length === 0) return <span className={styles.ic50None}>—</span>;
                                        const selSet = new Set((wes?.mutations || []).filter((m) => m.selected).map((m) => m.gene));
                                        const shown = all.filter((g) => MUT_REP_SET.has(g) || selSet.has(g));
                                        const rest = all.filter((g) => !MUT_REP_SET.has(g) && !selSet.has(g));
                                        return (
                                            <>
                                                {shown.map((m) => (
                                                    <span key={m} className={selSet.has(m) ? `${styles.mutToken} ${styles.mutSelBadge}` : styles.mutToken}>{m}</span>
                                                ))}
                                                {rest.length > 0 && (
                                                    <span className={styles.mutMore} title={rest.join(", ")}>+{rest.length}</span>
                                                )}
                                            </>
                                        );
                                    })()}
                                </div>
                                {wes?.mutations?.length > 0 ? (
                                    <table className={styles.miniTable}>
                                        <thead><tr><th>Gene</th><th>HGVSp</th><th>VAF</th><th>
                                            Impact <InfoTip text={"HIGH: 단백질 기능을 소실시킬 가능성이 큰 변이 (frameshift, stop_gained, start_lost, splice donor/acceptor 등)\n\nMODERATE: 단백질을 파괴하진 않으나 구조·기능을 변화시킬 수 있는 변이 (missense, inframe indel 등)"} />
                                        </th></tr></thead>
                                        <tbody>
                                            {wes.mutations.map((m, i) => (
                                                <tr key={i} className={m.selected ? styles.mutRowSelected : undefined}>
                                                    <td>{m.gene}{m.selected && <span className={styles.mutSelBadge}>선택</span>}</td>
                                                    <td className={styles.mono10}>{m.hgvspShort}</td>
                                                    <td className={styles.mono10}>{m.vaf?.toFixed?.(2) ?? m.vaf}</td><td>{m.impact}</td></tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : <div className={styles.fieldHint}>주요 유전자 변이만 표시 · 전체 변이는 NGS 분석 페이지에서 다운로드</div>}
                            </DrawerSection>
                            <DrawerSection title="WES metrics">
                                <DMetric label="Mapping rate" value={wes?.mapRate != null ? `${wes.mapRate}%` : "—"} />
                                <DMetric label="MSI-High" value={wes?.msiHigh ? "Yes" : "No"} />
                                <DMetric label="CNV count" value={wes?.cnvCount ?? "—"} />
                            </DrawerSection>
                        </>
                    )}

                    {tab === "RNA" && (
                        <DrawerSection title="RNA metrics">
                            <DMetric label="Mapping rate" value={rna?.mapRate != null ? `${rna.mapRate}%` : "—"} />
                            <DMetric label="Fusion count" value={rna?.fusions ?? group.fusions ?? "—"} />
                            <DMetric label="Expressed genes" value={rna?.expressedGene ?? "—"} />
                            {rna?.fusionList && rna.fusionList.length > 0 && (
                                <>
                                    <div className={styles.miniTableCaption}>
                                        Fusion <span className={styles.miniTableCount}>{rna.fusionList.length}</span>
                                    </div>
                                    {/* 건수가 많아 목록이 길어지므로 이 표만 따로 스크롤한다(헤더 고정) */}
                                    <div className={styles.miniTableScroll}>
                                        <table className={`${styles.miniTable} ${styles.miniTableCenter}`}>
                                            <thead>
                                                <tr><th>Fusion 1</th><th>Fusion 2</th><th>Type</th><th>Confidence</th></tr>
                                            </thead>
                                            <tbody>
                                                {rna.fusionList.map((f, i) => (
                                                    <tr key={i}>
                                                        <td>{f.gene1 || "—"}</td>
                                                        <td>{f.gene2 || "—"}</td>
                                                        <td>{f.type || "—"}</td>
                                                        <td>{f.confidence || "—"}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}
                            <div className={styles.fieldHint} style={{ marginTop: 8 }}>발현 상세는 NGS 분석 페이지에서 다운로드</div>
                        </DrawerSection>
                    )}

                    {tab === "Drug" && (
                        <DrugTab groupKey={group.groupKey} gbccKey={group.id}
                            drugs={Array.isArray(drug) ? drug : Object.keys(drug || {})}
                            selectedDrug={selectedDrug} setSelectedDrug={setSelectedDrug} />
                    )}
                </div>

                <div className={styles.drawerFooter}>
                    <button className={styles.ghostBtn}><Icon.External /> NGS 분석 페이지에서 상세 보기</button>
                </div>
            </aside>
        </>
    );
}

/* 상세 Drawer — 약물반응 탭 (IC50 계산·표시). 조회 전용.
 * 흐름: getChemicalData(responsValue) → parseText → 약물별 xdata/ydata
 *       → calculateIC50(수동수정 우선) → 표시.
 * IC50 수정은 오가노이드 상세 화면에서만 한다(통합검색은 조회 전용). */
export function DrugTab({ groupKey, gbccKey, drugs, selectedDrug, setSelectedDrug }) {
    const [parsed, setParsed] = useState(null);       // { 약물: {xdata,ydata} }
    const [ic50Map, setIc50Map] = useState({});       // { 약물: "45.0 nM" }
    const [loading, setLoading] = useState(false);

    // gbccKey 는 표시용(#), 저장/조회 key 는 hashmark 형이어야 하므로 복원
    const sampleKey = String(gbccKey || "").replace(/#/g, "hashmark");

    useEffect(() => {
        let alive = true;
        setLoading(true); setParsed(null); setIc50Map({});
        (async () => {
            try {
                // 1. responsValue 원본 가져오기
                const res = await fetch("/api/getChemicalData", {
                    method: "post", headers: { "content-type": "application/json" },
                    body: JSON.stringify({ param: sampleKey }),
                });
                const rows = await res.json();
                const text = rows && rows[0] && rows[0].responsValue;
                if (!text) { if (alive) setLoading(false); return; }

                // 2. 파싱 → { 약물: {xdata:[log농도], ydata:[viability]} }
                const p = parseDrugResponse(text);
                if (!alive) return;
                setParsed(p);

                // 3. 각 약물 IC50 계산 (수동수정 우선)
                const names = Object.keys(p);
                const out = {};
                await Promise.all(names.map(async (name) => {
                    try {
                        const r = await fetch("/api/calculateIC50", {
                            method: "post", headers: { "content-type": "application/json" },
                            body: JSON.stringify({ xdata: p[name].xdata, ydata: p[name].ydata, sampleKey, drugName: name }),
                        });
                        const d = await r.json();
                        out[name] = d && d.IC50 != null ? String(d.IC50) : "N/A";
                    } catch { out[name] = "N/A"; }
                }));
                if (!alive) return;
                setIc50Map(out);
                if (!selectedDrug && names.length) setSelectedDrug(names[0]);
            } catch (e) { /* noop */ }
            finally { if (alive) setLoading(false); }
        })();
        return () => { alive = false; };
    }, [sampleKey]); // eslint-disable-line

    const names = parsed ? Object.keys(parsed) : (drugs || []);

    // IC50 수정은 여기서 하지 않는다. 통합검색은 조회 전용이고,
    // 값 변경은 해당 오가노이드 상세 화면에서만 이뤄져야 한다(수정 이력·권한이 그쪽에 있음).
    //   → 기존 "Edit IC50" 버튼 / 저장 UI / /api/updateIC50 호출 제거.

    return (
        <DrawerSection title="Drug response (IC50)">
            {loading && <div className={styles.fieldHint}>IC50 계산 중…</div>}
            {!loading && names.length === 0 && <span className={styles.fieldHint}>약물반응 데이터 없음</span>}

            {names.length > 0 && (
                <>
                    <div className={styles.drugPickRow}>
                        <span className={styles.fieldLabel} style={{ margin: 0 }}>약물</span>
                        <select className={styles.drugSelectInline} value={selectedDrug || ""} onChange={(e) => setSelectedDrug(e.target.value || null)}>
                            <option value="">선택…</option>
                            {names.map((d) => <option key={d} value={d}>{d}</option>)}
                        </select>
                    </div>

                    <div className={styles.ic50Big}>
                        {selectedDrug
                            ? <><span className={styles.ic50BigVal}>{ic50Map[selectedDrug] ?? "—"}</span></>
                            : <span className={styles.fieldHint}>약물을 선택하면 IC50 이 표시됩니다</span>}
                    </div>

                    <div className={styles.drugListMini}>
                        {names.map((d) => (
                            <div key={d} className={d === selectedDrug ? `${styles.drugMiniItem} ${styles.drugMiniActive}` : styles.drugMiniItem}
                                onClick={() => setSelectedDrug(d)}>
                                <span>{d}</span>
                                <span className={styles.mono10}>{ic50Map[d] ?? "—"}</span>
                            </div>
                        ))}
                    </div>

                    <div className={styles.fieldHint} style={{ marginTop: 8 }}>정밀 곡선·그래프와 IC50 수정은 약물반응 상세 페이지에서</div>
                </>
            )}
        </DrawerSection>
    );
}

/* responsValue(텍스트) → { 약물: {xdata:[log10(nM)], ydata:[viability]} }
 * ★ 기존 Parse.js(parseText)와 동일 규칙:
 *   - 줄바꿈 분리, 각 줄을 \t 또는 , 로 분리
 *   - 첫 줄(헤더) 제거(slice(1))
 *   - [index, Chemical, nM, viability]
 *   - xdata = log10(parseFloat(nM)), ydata = viability(원본 그대로) */
export function parseDrugResponse(text) {
    const out = {};
    if (!text) return out;
    const rows = String(text).split('\n').map((row) => row.split(/\t|,/));
    rows.slice(1).forEach((row) => {
        const chemical = row[1];
        const nM = row[2];
        const viability = row[3];
        if (chemical == null || nM == null || viability == null) return;
        const chem = String(chemical).trim();
        if (!chem) return;
        const lognM = Math.log10(parseFloat(nM));
        if (isNaN(lognM)) return;
        if (!out[chem]) out[chem] = { xdata: [], ydata: [] };
        out[chem].xdata.push(lognM);
        out[chem].ydata.push(viability);
    });
    return out;
}

export function DrawerSection({ title, children }) {
    return <section className={styles.drawerSection}><div className={styles.drawerSectionTitle}>{title}</div>{children}</section>;
}
export function DGrid({ children }) { return <div className={styles.dGrid}>{children}</div>; }
export function DItem({ label, children }) {
    return <div><div className={styles.dLabel}>{label}</div><div className={styles.dValue}>{children}</div></div>;
}
export function DMetric({ label, value }) {
    return (
        <div className={styles.dMetric}>
            <span className={styles.dMetricLabel}>{label}</span>
            <span className={styles.dMetricValueWrap}><span className={styles.mapValue}>{value}</span></span>
        </div>
    );
}

/* =============================================================
 * 코호트 관리 모달
 * ============================================================= */

/* Impact 등 컬럼 설명 툴팁 (마우스 오버 시 팝업) */
export function InfoTip({ text }) {
    const [show, setShow] = useState(false);
    return (
        <span style={{ position: "relative", display: "inline-block" }}
            onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
            <span style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                width: 14, height: 14, borderRadius: "50%", border: "1px solid currentColor",
                fontSize: 9, cursor: "help", opacity: 0.7, verticalAlign: "middle",
            }}>?</span>
            {show && (
                <span style={{
                    position: "absolute", bottom: "130%", left: "50%", transform: "translateX(-50%)",
                    width: 260, padding: "8px 10px", borderRadius: 6, zIndex: 50,
                    background: "#1f2937", color: "#f9fafb", fontSize: 11, lineHeight: 1.5,
                    fontWeight: 400, whiteSpace: "pre-wrap", textAlign: "left",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
                }}>{text}</span>
            )}
        </span>
    );
}